import { useState, useEffect, useRef, useMemo, useCallback, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProduct } from '@/hooks/useProducts';
import { productsApi, type CreateProductInput } from '@/services/products';
import { adminApi } from '@/services/admin';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { generateThumbnail } from '@/utils/thumbnailRenderer';

const CATEGORIES = [
  { value: 'Enclosures', label: 'Enclosures' },
  { value: 'Gadgets', label: 'Gadgets' },
  { value: 'Tools', label: 'Tools' },
  { value: 'Decorative', label: 'Decorative' },
  { value: 'Mechanical', label: 'Mechanical' },
];

const ALLOWED_MODEL_EXTENSIONS = ['.stl', '.obj', '.3mf', '.step', '.stp'];
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_PREVIEW_IMAGES = 6;

// =============================================
// Main form page
// =============================================

export const AdminProductFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: existing, isLoading: loadingExisting } = useProduct(id ?? '');

  // Fetch materials from database
  const { data: materialsData, isLoading: loadingMaterials } = useQuery({
    queryKey: ['admin', 'materials'],
    queryFn: () => adminApi.getMaterials(),
    staleTime: 1000 * 60,
  });
  const activeMaterials = useMemo(
    () => (materialsData ?? []).filter((m) => m.isActive),
    [materialsData],
  );

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);

  // Model analysis state
  interface AnalysisResult {
    readonly volumeCm3: number;
    readonly surfaceAreaCm2: number;
    readonly boundingBox: { readonly width: number; readonly height: number; readonly depth: number };
    readonly prices: readonly { readonly materialId: string; readonly price: number; readonly rawPrice: number; readonly estimatedDays: number }[];
  }
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [analysisState, setAnalysisState] = useState<'idle' | 'uploading' | 'analyzing' | 'done' | 'error'>('idle');
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [priceManuallyEdited, setPriceManuallyEdited] = useState(false);
  const materialsInitialized = useRef(false);

  // Upload state
  const [modelFile, setModelFile] = useState<File | null>(null);
  const [modelUploadState, setModelUploadState] = useState<UploadState>({ status: 'idle' });
  const [existingFileKey, setExistingFileKey] = useState<string | null>(null);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);

  const [previewFiles, setPreviewFiles] = useState<(File | null)[]>([]);
  const [previewUploadStates, setPreviewUploadStates] = useState<UploadState[]>([]);
  const [existingPreviews, setExistingPreviews] = useState<readonly string[]>([]);

  // Snapshot generation state
  const [snapshotState, setSnapshotState] = useState<'idle' | 'generating' | 'done' | 'error'>('idle');
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  // General state
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && existing) {
      setName(existing.name);
      setDescription(existing.description);
      setCategory(existing.category);
      setBasePrice(String(existing.basePrice));
      setSelectedMaterials([...existing.materials]);
      setExistingFileKey(existing.fileKey);
      setExistingFileName(existing.originalFileName);
      setExistingPreviews(existing.previewImageKeys ?? []);
    }
  }, [isEditing, existing]);

  // Auto-select all active materials for new products
  useEffect(() => {
    if (!isEditing && !materialsInitialized.current && activeMaterials.length > 0) {
      setSelectedMaterials(activeMaterials.map((m) => m.materialId));
      materialsInitialized.current = true;
    }
  }, [isEditing, activeMaterials]);

  // Auto-set base price from analysis result (cheapest material)
  useEffect(() => {
    if (analysisResult && analysisResult.prices.length > 0 && !isEditing && !priceManuallyEdited) {
      const cheapest = Math.min(...analysisResult.prices.map((p) => p.price));
      setBasePrice(cheapest.toFixed(2));
    }
  }, [analysisResult, isEditing, priceManuallyEdited]);

  // Upload model file to temp S3 and trigger server-side analysis
  const handleModelFileSelected = async (file: File) => {
    setModelFile(file);
    setModelUploadState({ status: 'idle' });
    setAnalysisResult(null);
    setAnalysisError(null);
    setAnalysisState('idle');
    setSnapshotState('idle');
    setSnapshotError(null);

    if (!file.name.toLowerCase().endsWith('.3mf')) {
      return;
    }

    try {
      setAnalysisState('uploading');
      const { uploadUrl, fileKey } = await adminApi.getModelUploadUrl(
        file.name,
        file.size,
        file.type || 'application/octet-stream',
      );
      await uploadToS3(uploadUrl, file, file.type || 'application/octet-stream', () => {});

      setAnalysisState('analyzing');
      const result = await adminApi.analyzeModel(fileKey);
      setAnalysisResult(result);
      setAnalysisState('done');
    } catch (err) {
      setAnalysisState('error');
      setAnalysisError(err instanceof Error ? err.message : 'Analysis failed');
    }
  };

  const toggleMaterial = (mat: string) => {
    setSelectedMaterials((prev) =>
      prev.includes(mat) ? prev.filter((m) => m !== mat) : [...prev, mat],
    );
  };

  // Determine if a 3MF model is available for snapshot generation
  // Use fileKey as the source of truth for existing models (fileName can diverge)
  const has3mfModel =
    (modelFile !== null && modelFile.name.toLowerCase().endsWith('.3mf')) ||
    (modelFile === null && existingFileKey?.toLowerCase().endsWith('.3mf'));

  // Find the next empty preview slot
  const findNextAvailableSlot = useCallback((): number => {
    for (let i = 0; i < MAX_PREVIEW_IMAGES; i++) {
      const hasExisting = existingPreviews[i] && existingPreviews[i] !== '';
      const hasFile = previewFiles[i] != null;
      if (!hasExisting && !hasFile) return i;
    }
    return -1;
  }, [existingPreviews, previewFiles]);

  const handleGenerateSnapshot = useCallback(async () => {
    setSnapshotState('generating');
    setSnapshotError(null);

    let modelUrl = '';
    let isLocalUrl = false;

    try {
      if (modelFile && modelFile.name.toLowerCase().endsWith('.3mf')) {
        modelUrl = URL.createObjectURL(modelFile);
        isLocalUrl = true;
      } else if (isEditing && id && existingFileKey) {
        modelUrl = await productsApi.getFileDownloadUrl(id);
      } else {
        throw new Error('No 3MF model available');
      }

      // generateThumbnail fully consumes the URL synchronously via ThreeMFLoader
      // before resolving, so revoking the object URL in `finally` is safe.
      const blob = await generateThumbnail(modelUrl);
      const snapshotFile = new File([blob], 'snapshot.png', { type: 'image/png' });

      // Resolve slot inside the updater to avoid stale-state race.
      // Capture existingPreviews in the closure to also check occupied slots.
      const currentExisting = existingPreviews;
      let resolvedSlot = -1;

      setPreviewFiles((prev) => {
        const next = [...prev];
        for (let i = 0; i < MAX_PREVIEW_IMAGES; i++) {
          const hasExisting = currentExisting[i] && currentExisting[i] !== '';
          const hasFile = next[i] != null;
          if (!hasExisting && !hasFile) { resolvedSlot = i; break; }
        }
        if (resolvedSlot === -1) return prev;
        while (next.length <= resolvedSlot) next.push(null);
        next[resolvedSlot] = snapshotFile;
        return next;
      });

      if (resolvedSlot !== -1) {
        setPreviewUploadStates((prev) => {
          const next = [...prev];
          while (next.length <= resolvedSlot) next.push({ status: 'idle' });
          next[resolvedSlot] = { status: 'idle' };
          return next;
        });
      }

      setSnapshotState('done');
    } catch (err) {
      setSnapshotState('error');
      setSnapshotError(err instanceof Error ? err.message : 'Snapshot generation failed');
    } finally {
      if (isLocalUrl && modelUrl) {
        URL.revokeObjectURL(modelUrl);
      }
    }
  }, [modelFile, isEditing, id, existingFileKey, existingPreviews]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const price = parseFloat(basePrice);
    if (isNaN(price) || price <= 0) {
      setError('Price must be a positive number');
      return;
    }

    if (selectedMaterials.length === 0) {
      setError('Select at least one material');
      return;
    }

    // Convert analysis bounding box (cm) to product dimensions (mm)
    const dimensions = analysisResult?.boundingBox
      ? {
          width: Math.round(analysisResult.boundingBox.width * 10 * 10) / 10,
          height: Math.round(analysisResult.boundingBox.height * 10 * 10) / 10,
          depth: Math.round(analysisResult.boundingBox.depth * 10 * 10) / 10,
        }
      : null;

    const input: CreateProductInput = {
      name,
      description,
      category,
      basePrice: price,
      materials: selectedMaterials,
      ...(dimensions ? { dimensions } : {}),
    };

    setIsSubmitting(true);
    try {
      let productId: string;

      if (isEditing && id) {
        await productsApi.update(id, input);
        productId = id;
      } else {
        const created = await productsApi.create(input);
        productId = created.productId;
      }

      // Upload model file if selected
      if (modelFile) {
        await uploadModelFile(productId, modelFile, setModelUploadState);
      }

      // Upload preview images
      const filesToUpload = previewFiles
        .map((file, idx) => ({ file, idx }))
        .filter((entry): entry is { file: File; idx: number } => entry.file !== null);

      for (const { file, idx } of filesToUpload) {
        await uploadPreviewImage(productId, file, idx, (state) => {
          setPreviewUploadStates((prev) => {
            const next = [...prev];
            next[idx] = state;
            return next;
          });
        });
      }

      // Persist final previewImageKeys (handles removals and new uploads)
      const finalKeys: string[] = [];
      for (let i = 0; i < MAX_PREVIEW_IMAGES; i++) {
        const uploaded = filesToUpload.find((f) => f.idx === i);
        if (uploaded) {
          const ext = uploaded.file.type === 'image/png' ? 'png'
            : uploaded.file.type === 'image/webp' ? 'webp' : 'jpg';
          finalKeys.push(`products/${productId}/preview-${i}.${ext}`);
        } else if (existingPreviews[i] && existingPreviews[i] !== '') {
          finalKeys.push(existingPreviews[i]);
        } else {
          finalKeys.push('');
        }
      }
      // Trim trailing empty strings
      while (finalKeys.length > 0 && finalKeys[finalKeys.length - 1] === '') {
        finalKeys.pop();
      }
      await productsApi.update(productId, { previewImageKeys: finalKeys });

      await queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate('/admin/products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditing && loadingExisting) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const anyUploading =
    modelUploadState.status === 'uploading' ||
    previewUploadStates.some((s) => s.status === 'uploading') ||
    analysisState === 'uploading' ||
    analysisState === 'analyzing' ||
    snapshotState === 'generating';

  return (
    <div style={{ maxWidth: '720px' }}>
      <h1
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          marginBottom: 'var(--space-6)',
        }}
      >
        {isEditing ? 'Edit Product' : 'Add Product'}
      </h1>

      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}
      >
        {error && (
          <div
            style={{
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-error-50)',
              padding: 'var(--space-3) var(--space-4)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-error-700)',
            }}
          >
            {error}
          </div>
        )}

        {/* --- Product Details Card --- */}
        <FormCard title="Product Details">
          <Input
            id="name"
            label="Product Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <div>
            <label
              htmlFor="description"
              style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-secondary)',
              }}
            >
              Description
            </label>
            <textarea
              id="description"
              required
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="admin-input"
              style={{
                marginTop: 'var(--space-1)',
                display: 'block',
                width: '100%',
                resize: 'vertical',
              }}
            />
          </div>

          <Select
            id="category"
            label="Category"
            options={CATEGORIES}
            placeholder="Select a category"
            required
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-secondary)',
                marginBottom: 'var(--space-2)',
              }}
            >
              Compatible Materials
            </label>
            {loadingMaterials ? (
              <div style={{ padding: 'var(--space-2)', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                Loading materials...
              </div>
            ) : activeMaterials.length === 0 ? (
              <div style={{ padding: 'var(--space-2)', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                No materials configured. Add materials in the Materials page first.
              </div>
            ) : (
              <div className="flex flex-wrap" style={{ gap: 'var(--space-2)' }}>
                {activeMaterials.map((mat) => (
                  <button
                    key={mat.materialId}
                    type="button"
                    onClick={() => toggleMaterial(mat.materialId)}
                    className="focus-ring"
                    style={{
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      border: 'none',
                      cursor: 'pointer',
                      minHeight: '36px',
                      backgroundColor: selectedMaterials.includes(mat.materialId)
                        ? 'var(--color-primary-600)'
                        : 'var(--bg-surface-sunken)',
                      color: selectedMaterials.includes(mat.materialId)
                        ? 'var(--text-inverse)'
                        : 'var(--text-secondary)',
                      transition: 'all var(--duration-fast) var(--ease-out)',
                    }}
                  >
                    {mat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </FormCard>

        {/* --- 3D Model File + Analysis Card --- */}
        <FormCard title="3D Model File">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', margin: 0 }}>
            Upload a 3MF file to automatically calculate volume, dimensions, and pricing. Other formats (STL, OBJ, STEP) are stored but not analyzed.
          </p>

          {existingFileKey && !modelFile && (
            <ExistingFileBadge
              label={existingFileName ?? existingFileKey.split('/').pop() ?? 'Model file'}
              onRemove={() => {
                setExistingFileKey(null);
                setExistingFileName(null);
              }}
            />
          )}

          <ModelFileDropzone
            file={modelFile}
            uploadState={modelUploadState}
            onFileSelected={handleModelFileSelected}
            onClear={() => {
              setModelFile(null);
              setModelUploadState({ status: 'idle' });
              setAnalysisResult(null);
              setAnalysisState('idle');
              setAnalysisError(null);
              setSnapshotState('idle');
              setSnapshotError(null);
            }}
          />

          {(analysisState === 'uploading' || analysisState === 'analyzing') && (
            <div className="flex items-center" style={{ gap: 'var(--space-2)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-surface-sunken)' }}>
              <LoadingSpinner size="sm" />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                {analysisState === 'uploading' ? 'Uploading for analysis...' : 'Analyzing model geometry and calculating prices...'}
              </span>
            </div>
          )}

          {analysisState === 'error' && (
            <div style={{ borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-error-50)', padding: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error-700)' }}>
              {analysisError ?? 'Analysis failed'}
            </div>
          )}

          {analysisResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div
                className="grid"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--space-2)' }}
              >
                <AnalysisStat label="Volume" value={`${analysisResult.volumeCm3} cm3`} />
                <AnalysisStat label="Surface Area" value={`${analysisResult.surfaceAreaCm2} cm2`} />
                <AnalysisStat label="Width" value={`${(analysisResult.boundingBox.width * 10).toFixed(1)} mm`} />
                <AnalysisStat label="Height" value={`${(analysisResult.boundingBox.height * 10).toFixed(1)} mm`} />
                <AnalysisStat label="Depth" value={`${(analysisResult.boundingBox.depth * 10).toFixed(1)} mm`} />
              </div>
              <AnalysisPriceDisplay prices={analysisResult.prices} activeMaterials={activeMaterials} />
            </div>
          )}
        </FormCard>

        {/* --- Base Price Card --- */}
        <FormCard title="Pricing">
          <Input
            id="price"
            label="Base Price ($)"
            type="number"
            step="0.01"
            min="0.01"
            required
            value={basePrice}
            onChange={(e) => {
              setBasePrice(e.target.value);
              setPriceManuallyEdited(true);
            }}
          />
          {analysisResult && analysisResult.prices.length > 0 && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: 0 }}>
              {priceManuallyEdited
                ? 'Price manually set.'
                : 'Auto-filled with the cheapest material price. You can override this.'}
              {priceManuallyEdited && (
                <button
                  type="button"
                  onClick={() => {
                    setPriceManuallyEdited(false);
                    const cheapest = Math.min(...analysisResult.prices.map((p) => p.price));
                    setBasePrice(cheapest.toFixed(2));
                  }}
                  style={{
                    marginLeft: 'var(--space-2)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-accent)',
                    fontSize: 'var(--text-xs)',
                    textDecoration: 'underline',
                    padding: 0,
                  }}
                >
                  Reset to calculated
                </button>
              )}
            </p>
          )}
        </FormCard>

        {/* --- Preview Images Card --- */}
        <FormCard title="Preview Images">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', margin: 0 }}>
            Upload up to {MAX_PREVIEW_IMAGES} preview images. The first image is the primary thumbnail. JPEG, PNG, or WebP.
          </p>

          {/* Snapshot generation from 3D model */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface-sunken)',
            }}
          >
            <CameraIcon />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', margin: 0 }}>
                Generate from 3D Model
              </p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: '2px 0 0' }}>
                {has3mfModel
                  ? 'Render a snapshot from the 3MF model and add it as a preview image.'
                  : 'Upload a 3MF model file to enable snapshot generation.'}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={!has3mfModel || snapshotState === 'generating' || findNextAvailableSlot() === -1}
              onClick={handleGenerateSnapshot}
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {snapshotState === 'generating' ? 'Generating...' : 'Generate Snapshot'}
            </Button>
          </div>

          {snapshotState === 'generating' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              <LoadingSpinner size="sm" />
              <span>Rendering 3D model snapshot...</span>
            </div>
          )}

          {snapshotState === 'done' && (
            <div
              style={{
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--color-success-50)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-success-600)',
              }}
            >
              Snapshot generated and added to preview images.
            </div>
          )}

          {snapshotState === 'error' && (
            <div
              style={{
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--color-error-50)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-error-700)',
              }}
            >
              {snapshotError ?? 'Snapshot generation failed'}
            </div>
          )}

          <PreviewImagesGrid
            existingKeys={existingPreviews}
            files={previewFiles}
            uploadStates={previewUploadStates}
            onFileSelected={(file, index) => {
              setPreviewFiles((prev) => {
                const next = [...prev];
                while (next.length <= index) next.push(null);
                next[index] = file;
                return next;
              });
              setPreviewUploadStates((prev) => {
                const next = [...prev];
                while (next.length <= index) next.push({ status: 'idle' });
                next[index] = { status: 'idle' };
                return next;
              });
            }}
            onRemove={(index) => {
              setPreviewFiles((prev) => {
                const next = [...prev];
                next[index] = null;
                return next;
              });
              setExistingPreviews((prev) => {
                const next = [...prev];
                if (index < next.length) {
                  next[index] = '';
                }
                return next;
              });
            }}
          />
        </FormCard>

        {/* --- Actions --- */}
        <div className="flex" style={{ gap: 'var(--space-3)' }}>
          <Button type="submit" disabled={isSubmitting || anyUploading}>
            {isSubmitting
              ? 'Saving...'
              : isEditing
                ? 'Update Product'
                : 'Create Product'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/products')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

// =============================================
// Upload helpers
// =============================================

interface UploadState {
  readonly status: 'idle' | 'uploading' | 'done' | 'error';
  readonly progress?: number;
  readonly error?: string;
}

const uploadModelFile = async (
  productId: string,
  file: File,
  setState: (s: UploadState) => void,
) => {
  setState({ status: 'uploading', progress: 0 });

  const { uploadUrl } = await productsApi.getFileUploadUrl(
    productId,
    file.name,
    file.size,
    file.type || 'application/octet-stream',
  );

  await uploadToS3(uploadUrl, file, file.type || 'application/octet-stream', (pct) => {
    setState({ status: 'uploading', progress: pct });
  });

  setState({ status: 'done', progress: 100 });
};

const uploadPreviewImage = async (
  productId: string,
  file: File,
  index: number,
  setState: (s: UploadState) => void,
) => {
  setState({ status: 'uploading', progress: 0 });

  const contentType = file.type || 'image/jpeg';
  const { uploadUrl } = await productsApi.getImageUploadUrl(productId, index, contentType);

  await uploadToS3(uploadUrl, file, contentType, (pct) => {
    setState({ status: 'uploading', progress: pct });
  });

  setState({ status: 'done', progress: 100 });
};

const uploadToS3 = (
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.send(file);
  });
};

// =============================================
// Form card wrapper
// =============================================

const FormCard = ({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-5)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-4)',
    }}
  >
    <h2
      style={{
        fontSize: 'var(--text-base)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-primary)',
        margin: 0,
      }}
    >
      {title}
    </h2>
    {children}
  </div>
);

// =============================================
// Existing file badge
// =============================================

const ExistingFileBadge = ({
  label,
  onRemove,
}: {
  readonly label: string;
  readonly onRemove: () => void;
}) => (
  <div
    className="flex items-center"
    style={{
      gap: 'var(--space-2)',
      padding: 'var(--space-2) var(--space-3)',
      borderRadius: 'var(--radius-md)',
      backgroundColor: 'var(--color-success-50)',
      border: '1px solid var(--color-success-500)',
      fontSize: 'var(--text-sm)',
      color: 'var(--color-success-600)',
    }}
  >
    <span style={{ flex: 1, fontWeight: 'var(--font-weight-medium)' }}>{label}</span>
    <button
      type="button"
      onClick={onRemove}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'var(--text-xs)',
        color: 'var(--text-tertiary)',
        padding: '2px 4px',
      }}
    >
      Remove
    </button>
  </div>
);

// =============================================
// Model file dropzone
// =============================================

const ModelFileDropzone = ({
  file,
  uploadState,
  onFileSelected,
  onClear,
}: {
  readonly file: File | null;
  readonly uploadState: UploadState;
  readonly onFileSelected: (f: File) => void;
  readonly onClear: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && isValidModelFile(dropped.name)) {
      onFileSelected(dropped);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      onFileSelected(selected);
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  if (file) {
    return (
      <div
        className="flex items-center"
        style={{
          gap: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface-sunken)',
        }}
      >
        <FileIcon />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-primary)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.name}
          </p>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: 0 }}>
            {formatFileSize(file.size)}
          </p>
          {uploadState.status === 'uploading' && (
            <ProgressBar progress={uploadState.progress ?? 0} />
          )}
        </div>
        {uploadState.status !== 'uploading' && (
          <button
            type="button"
            onClick={onClear}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-tertiary)',
              fontSize: 'var(--text-sm)',
              padding: '4px',
            }}
          >
            X
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_MODEL_EXTENSIONS.join(',')}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${isDragOver ? 'var(--color-primary-400)' : 'var(--border-default)'}`,
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragOver ? 'var(--color-primary-50)' : 'transparent',
          transition: 'all var(--duration-fast) var(--ease-out)',
        }}
      >
        <FileIcon style={{ margin: '0 auto var(--space-2)' }} />
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
          Drop a file here or <span style={{ color: 'var(--text-accent)', fontWeight: 'var(--font-weight-medium)' }}>browse</span>
        </p>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
          STL, OBJ, 3MF, STEP — max 50MB
        </p>
      </div>
    </>
  );
};

// =============================================
// Preview images grid
// =============================================

const PreviewImagesGrid = ({
  existingKeys,
  files,
  uploadStates,
  onFileSelected,
  onRemove,
}: {
  readonly existingKeys: readonly string[];
  readonly files: (File | null)[];
  readonly uploadStates: UploadState[];
  readonly onFileSelected: (file: File, index: number) => void;
  readonly onRemove: (index: number) => void;
}) => {
  // Determine how many slots to show: existing count, files count, or at least 1 empty slot
  const filledCount = Math.max(
    existingKeys.filter((k) => k !== '').length,
    files.filter((f) => f !== null).length,
  );
  const slotCount = Math.min(filledCount + 1, MAX_PREVIEW_IMAGES);

  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 140px), 1fr))',
        gap: 'var(--space-3)',
      }}
    >
      {Array.from({ length: slotCount }, (_, i) => (
        <PreviewSlot
          key={i}
          index={i}
          file={files[i] ?? null}
          existingKey={existingKeys[i] ?? ''}
          uploadState={uploadStates[i] ?? { status: 'idle' }}
          onFileSelected={(f) => onFileSelected(f, i)}
          onRemove={() => onRemove(i)}
          isPrimary={i === 0}
        />
      ))}
    </div>
  );
};

const PreviewSlot = ({
  index,
  file,
  existingKey,
  uploadState,
  onFileSelected,
  onRemove,
  isPrimary,
}: {
  readonly index: number;
  readonly file: File | null;
  readonly existingKey: string;
  readonly uploadState: UploadState;
  readonly onFileSelected: (f: File) => void;
  readonly onRemove: () => void;
  readonly isPrimary: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Create object URL for local preview
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [file]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected && ALLOWED_IMAGE_TYPES.includes(selected.type)) {
      onFileSelected(selected);
    }
    e.target.value = '';
  };

  const hasContent = file || (existingKey && existingKey !== '');
  const displayUrl = previewUrl ?? (existingKey ? `/product-images/${existingKey}` : null);

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_IMAGE_TYPES.join(',')}
        onChange={handleChange}
        style={{ display: 'none' }}
      />

      {hasContent && displayUrl ? (
        <div
          style={{
            position: 'relative',
            aspectRatio: '1',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            overflow: 'hidden',
            backgroundColor: 'var(--bg-surface-sunken)',
          }}
        >
          <img
            src={displayUrl}
            alt={`Preview ${index + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />

          {isPrimary && (
            <span
              style={{
                position: 'absolute',
                top: '6px',
                left: '6px',
                fontSize: '0.6rem',
                fontWeight: 'var(--font-weight-bold)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                padding: '2px 6px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--color-primary-600)',
                color: 'var(--text-inverse)',
              }}
            >
              Primary
            </span>
          )}

          {/* Replace / remove overlay */}
          <div
            className="flex items-center justify-center"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              opacity: 0,
              transition: 'opacity var(--duration-fast) var(--ease-out)',
              gap: 'var(--space-2)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
          >
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              style={{
                background: 'var(--color-white)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                padding: '4px 10px',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                color: 'var(--text-primary)',
              }}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onRemove}
              style={{
                background: 'var(--color-error-600)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                padding: '4px 10px',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                color: 'var(--text-inverse)',
              }}
            >
              Remove
            </button>
          </div>

          {uploadState.status === 'uploading' && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '4px',
                backgroundColor: 'rgba(0,0,0,0.4)',
              }}
            >
              <ProgressBar progress={uploadState.progress ?? 0} />
            </div>
          )}
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          style={{
            aspectRatio: '1',
            borderRadius: 'var(--radius-md)',
            border: '2px dashed var(--border-default)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: 'transparent',
            transition: 'border-color var(--duration-fast) var(--ease-out)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary-300)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
        >
          <span
            style={{
              fontSize: 'var(--text-2xl)',
              color: 'var(--text-tertiary)',
              lineHeight: 1,
            }}
          >
            +
          </span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            {isPrimary ? 'Primary' : `Image ${index + 1}`}
          </span>
        </div>
      )}
    </div>
  );
};

// =============================================
// Shared small components
// =============================================

const AnalysisStat = ({ label, value }: { readonly label: string; readonly value: string }) => (
  <div
    style={{
      padding: 'var(--space-2) var(--space-3)',
      borderRadius: 'var(--radius-md)',
      backgroundColor: 'var(--bg-surface-sunken)',
      border: '1px solid var(--border-default)',
    }}
  >
    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </p>
    <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', margin: '2px 0 0' }}>
      {value}
    </p>
  </div>
);

const AnalysisPriceDisplay = ({
  prices,
  activeMaterials,
}: {
  readonly prices: readonly { readonly materialId: string; readonly price: number }[];
  readonly activeMaterials: readonly { readonly materialId: string; readonly name: string }[];
}) => {
  const cheapest = Math.min(...prices.map((p) => p.price));
  const materialNames = new Map(activeMaterials.map((m) => [m.materialId, m.name]));

  return (
    <div
      style={{
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface-sunken)',
        padding: 'var(--space-3)',
      }}
    >
      <p
        style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-secondary)',
          margin: '0 0 var(--space-2) 0',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Calculated Prices by Material
      </p>
      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 'var(--space-2)',
        }}
      >
        {prices.map((p) => (
          <div
            key={p.materialId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              backgroundColor: p.price === cheapest ? 'var(--color-success-50)' : 'var(--bg-surface)',
              border: p.price === cheapest ? '1px solid var(--color-success-500)' : '1px solid var(--border-default)',
            }}
          >
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
              {materialNames.get(p.materialId) ?? p.materialId}
            </span>
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: p.price === cheapest ? 'var(--color-success-600)' : 'var(--text-primary)',
              }}
            >
              ${p.price.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProgressBar = ({ progress }: { readonly progress: number }) => (
  <div
    style={{
      height: '3px',
      borderRadius: '2px',
      backgroundColor: 'rgba(255,255,255,0.3)',
      overflow: 'hidden',
      marginTop: '4px',
    }}
  >
    <div
      style={{
        height: '100%',
        width: `${progress}%`,
        borderRadius: '2px',
        backgroundColor: 'var(--color-primary-500)',
        transition: 'width 150ms ease',
      }}
    />
  </div>
);

const CameraIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
  >
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const FileIcon = ({ style }: { readonly style?: React.CSSProperties }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: 'var(--text-tertiary)', ...style }}
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// =============================================
// Utility functions
// =============================================

const isValidModelFile = (name: string): boolean => {
  const lower = name.toLowerCase();
  return ALLOWED_MODEL_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
